import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Card } from '@shared/contracts/entities';
import {
  allowedCardActions,
  getCard,
  performCardAction,
  requestStepUp,
  revealCard,
  setCardControls,
  setCardLimit,
  verifySca,
  type CardAction,
  type RevealedCard,
} from '@/mocks/api';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { newIdempotencyKey } from '@/services/idempotency';
import { auditEventsFor, auditSummary } from '@/services/audit';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, DetailList, SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { ConfirmSheet, Sheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { formatDateTime, maskedPan } from '@/services/formatters';
import { isMockApiError } from '@/mocks/errors';

/**
 * Card detail and security controls — WF-SMEM-08.
 *
 * TRACE:TT-R138-01 — the card detail exists at /mobile/cards/:cardId.
 * TRACE:XS-SMEM-WR-128 — controls that depend on the card provider are labelled
 * as provider-gated rather than presented as guaranteed capability.
 * TRACE:WR-091-01..05 — wallet provisioning is shown with its eligibility and
 * lifecycle, explicitly marked as dependent on a separate tokenisation /
 * push-provisioning integration that is not confirmed in this package.
 */

const ACTION_COPY: Record<
  CardAction,
  { label: string; title: string; body: string; confirm: string; danger?: boolean }
> = {
  freeze: {
    label: 'Freeze card',
    title: 'Freeze this card?',
    body: 'Payments will be declined until you unfreeze it. You can unfreeze at any time.',
    confirm: 'Freeze card',
  },
  unfreeze: {
    label: 'Unfreeze card',
    title: 'Unfreeze this card?',
    body: 'The card will start accepting payments again immediately.',
    confirm: 'Unfreeze card',
  },
  block: {
    label: 'Block permanently',
    title: 'Block this card permanently?',
    body: 'This cannot be undone. The card will stop working straight away and you will need a replacement.',
    confirm: 'Block permanently',
    danger: true,
  },
  activate: {
    label: 'Activate card',
    title: 'Activate this card?',
    body: 'Confirm you have received the physical card. It will be ready to use straight away.',
    confirm: 'Activate card',
  },
  'request-replacement': {
    label: 'Request a replacement',
    title: 'Request a replacement card?',
    body: 'A new card will be ordered and posted to your registered address. The old card stays blocked.',
    confirm: 'Request replacement',
  },
};

export function CardDetailPage() {
  const { cardId = '' } = useParams();
  const resolved = resolveContext();
  const toast = useToast();

  const [pendingAction, setPendingAction] = useState<CardAction | null>(null);
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitValue, setLimitValue] = useState('');
  const [limitError, setLimitError] = useState<string | null>(null);

  // Reveal flow state.
  const [revealOpen, setRevealOpen] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealedCard | null>(null);
  const [countdown, setCountdown] = useState(0);

  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const card = useAsync(async () => {
    if (!companyId) throw new Error('no session');
    return (await getCard(companyId, cardId)).data;
  }, [companyId, cardId]);

  const actionMutation = useMutation(async (action: CardAction) => {
    if (!companyId || !userId) throw new Error('no session');
    return (
      await performCardAction(companyId, userId, cardId, action, newIdempotencyKey('card'))
    ).data;
  });

  const controlsMutation = useMutation(async (patch: Partial<Card['controls']>) => {
    if (!companyId || !userId) throw new Error('no session');
    return (await setCardControls(companyId, userId, cardId, patch)).data;
  });

  const limitMutation = useMutation(async (minor: number) => {
    if (!companyId || !userId) throw new Error('no session');
    return (await setCardLimit(companyId, userId, cardId, minor)).data;
  });

  const stepUpMutation = useMutation(async () => {
    if (!userId) throw new Error('no session');
    return (await requestStepUp(userId, `card-reveal:${cardId}`)).data;
  });

  const revealMutation = useMutation(async (verifiedPurpose: string) => {
    if (!companyId || !userId) throw new Error('no session');
    return (await revealCard(companyId, userId, cardId, verifiedPurpose)).data;
  });

  // Revealed details self-destruct; they are never cached or re-shown.
  useEffect(() => {
    if (!revealed) return undefined;
    setCountdown(revealed.expiresInSeconds);
    const tick = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          setRevealed(null);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [revealed]);

  if (!resolved) return null;

  const ctx = resolved.entitlements;
  const canManage = resolveCapability(ctx, CAPABILITIES.cardsManage).allowed;
  const canReveal = resolveCapability(ctx, CAPABILITIES.cardsReveal).allowed;

  if (card.status === 'loading') {
    return (
      <>
        <MobileHeader title="Card" backTo="/mobile/cards" backLabel="Cards" />
        <LoadingRegion label="Loading card">
          <div className="tm-stack">
            <CardSkeleton height={170} />
            <CardSkeleton height={200} />
          </div>
        </LoadingRegion>
      </>
    );
  }

  if (card.status === 'error') {
    return (
      <>
        <MobileHeader title="Card" backTo="/mobile/cards" backLabel="Cards" />
        <ErrorState error={card.error} onRetry={card.reload} title="We could not load this card" />
      </>
    );
  }

  const c: Card = card.data;
  const actions = allowedCardActions(c);
  // A block imposed by the bank is terminal: the customer unfreeze is absent,
  // not merely disabled, and the reason is always shown.
  const operatorBlocked =
    c.lifecycleStatus === 'blocked' && (c.statusReason?.includes('your bank') ?? false);
  const history = auditEventsFor(resolved.company.id, 'card', c.id);
  const controlsLocked = c.lifecycleStatus === 'blocked' || c.lifecycleStatus === 'cancelled';

  const startReveal = () => {
    setRevealed(null);
    setCode('');
    setCodeError(null);
    setRevealOpen(true);
    void stepUpMutation.run().then((outcome) => {
      if (outcome.ok) setChallengeId(outcome.value.challengeId);
    });
  };

  const submitReveal = async () => {
    if (!challengeId) return;
    setCodeError(null);

    let purpose: string;
    try {
      purpose = (await verifySca(challengeId, code)).data.purpose;
    } catch (e: unknown) {
      setCodeError(isMockApiError(e) ? e.message : 'That code was not correct.');
      setCode('');
      return;
    }

    const outcome = await revealMutation.run(purpose);
    if (outcome.ok) {
      setRevealed(outcome.value);
      setCode('');
    } else {
      setCodeError('We could not show the card details. Try again.');
    }
  };

  return (
    <>
      <MobileHeader title={`Card ${c.last4}`} backTo="/mobile/cards" backLabel="Cards" />

      <div className="tm-stack">
        {/* Card face. Sensitive data is masked by default. */}
        <div className="tm-card tm-card--accent tm-cardface">
          <div className="tm-between">
            <span className="tm-eyebrow">{c.cardType === 'virtual' ? 'Virtual card' : 'Physical card'}</span>
            <StatusBadge status={c.lifecycleStatus} />
          </div>
          <p className="tm-cardface__pan tm-amount">
            {revealed ? revealed.pan : maskedPan(c.last4)}
          </p>
          <div className="tm-between">
            <span>{c.cardholderName}</span>
            <span className="tm-amount">{revealed ? revealed.expiry : '••/••'}</span>
          </div>
          {revealed ? (
            <p className="tm-muted" style={{ margin: '8px 0 0' }} role="status">
              Security code {revealed.cvv} · hides in {countdown}s
            </p>
          ) : null}
        </div>

        {c.statusReason ? (
          <Notice
            tone={c.lifecycleStatus === 'blocked' ? 'danger' : 'warning'}
            title={`Card ${c.lifecycleStatus}`}
          >
            {c.statusReason}
            {operatorBlocked ? (
              <p style={{ margin: '6px 0 0' }}>
                This block was applied by your bank and cannot be lifted in the app.
              </p>
            ) : null}
          </Notice>
        ) : null}

        {/* Secure reveal (step-up authenticated). */}
        {canReveal && !controlsLocked ? (
          <button type="button" className="tm-btn tm-btn--secondary tm-btn--block" onClick={startReveal}>
            Show card details
          </button>
        ) : null}

        {/* Lifecycle actions. */}
        {canManage && actions.length > 0 ? (
          <section aria-labelledby="card-actions">
            <SectionHeader title="Card actions" id="card-actions" />
            <div className="tm-stack tm-stack--tight">
              {actions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={`tm-btn tm-btn--block${ACTION_COPY[action].danger ? ' tm-btn--danger' : ' tm-btn--secondary'}`}
                  onClick={() => setPendingAction(action)}
                >
                  {ACTION_COPY[action].label}
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {/* Spend controls. */}
        {canManage ? (
          <section aria-labelledby="card-controls">
            <SectionHeader title="Where this card works" id="card-controls" />
            <div className="tm-card">
              {controlsLocked ? (
                <p className="tm-muted" style={{ margin: 0 }}>
                  Controls cannot be changed on a blocked card.
                </p>
              ) : (
                <ul className="tm-rows tm-rows--plain">
                  {(
                    [
                      ['online', 'Online payments'],
                      ['contactless', 'Contactless'],
                      ['atm', 'Cash withdrawals'],
                      ['international', 'Outside the UK'],
                    ] as const
                  ).map(([key, label]) => (
                    <li key={key} className="tm-between tm-toggle-row">
                      <label className="tm-label" htmlFor={`ctl-${key}`}>
                        {label}
                      </label>
                      <input
                        id={`ctl-${key}`}
                        type="checkbox"
                        role="switch"
                        className="tm-switch"
                        checked={c.controls[key]}
                        disabled={controlsMutation.pending}
                        onChange={(e) => {
                          const next = { [key]: e.target.checked } as Partial<Card['controls']>;
                          void controlsMutation.run(next).then((outcome) => {
                            if (outcome.ok) {
                              card.setData(outcome.value);
                              toast.show({
                                message: `${label} ${e.target.checked ? 'turned on' : 'turned off'}.`,
                              });
                            }
                          });
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ) : null}

        {/* Spending limit. */}
        {canManage && !controlsLocked ? (
          <section aria-labelledby="card-limit">
            <SectionHeader title="Spending limit" id="card-limit" />
            <div className="tm-card tm-between">
              <div>
                <Amount minorUnits={c.spendingLimit} currency={c.currency} size="lg" />
                <p className="tm-hint" style={{ margin: '2px 0 0' }}>
                  Per month
                </p>
              </div>
              <button
                type="button"
                className="tm-btn tm-btn--secondary tm-btn--sm"
                onClick={() => {
                  setLimitValue(String(c.spendingLimit / 100));
                  setLimitError(null);
                  setLimitOpen(true);
                }}
              >
                Change
              </button>
            </div>
          </section>
        ) : null}

        {/* PIN — provider-gated, stated honestly. */}
        <section aria-labelledby="card-pin">
          <SectionHeader title="PIN" id="card-pin" />
          <div className="tm-card">
            <p className="tm-muted" style={{ margin: 0 }}>
              {c.cardType === 'virtual'
                ? 'Virtual cards do not have a PIN.'
                : 'PIN retrieval and PIN change depend on the card provider.'}
            </p>
            {c.cardType === 'physical' ? (
              <p className="tm-hint" style={{ marginBlockStart: 6 }}>
                Not found in Wallet &amp; Merchant API v1; requires confirmation from Issuer
                Processing, Risk Monitor, Programme Manager APIs or Tribe Hub.
              </p>
            ) : null}
          </div>
        </section>

        {/* Wallet provisioning — integration contract required. */}
        <section aria-labelledby="card-wallet">
          <SectionHeader title="Apple Pay and Google Pay" id="card-wallet" />
          <div className="tm-card">
            <DetailList
              items={[
                {
                  label: 'Eligibility',
                  value:
                    c.cardType === 'physical' && c.lifecycleStatus === 'active' ? (
                      <StatusBadge status="active" label="Eligible" tone="success" />
                    ) : (
                      <StatusBadge status="not-required" label="Not eligible" tone="neutral" />
                    ),
                },
                {
                  label: 'Provisioning status',
                  value: <StatusBadge status="not-requested" label="Not provisioned" tone="neutral" />,
                },
              ]}
            />
            <Notice tone="info" title="Simulated in this build">
              Adding a card to a mobile wallet depends on a separate tokenisation and
              push-provisioning integration. That integration is not confirmed in this package, so
              this area shows the states only and cannot complete a real provisioning.
            </Notice>
          </div>
        </section>

        {/* Corporate card usage entry point. */}
        <section aria-labelledby="card-expenses">
          <SectionHeader title="Expenses and receipts" id="card-expenses" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              <li>
                <Link className="tm-row" to="/mobile/expenses">
                  <span className="tm-row__body">
                    <span className="tm-row__title">Card usage and receipts</span>
                    <span className="tm-row__meta">
                      Report spend on this card in the expense module
                    </span>
                  </span>
                </Link>
              </li>
            </ul>
          </div>
          <p className="tm-hint" style={{ marginBlockStart: 8 }}>
            Expense management is a separate module. Whether a claim is triggered by a card
            authorisation or by an IBAN transfer is an open decision recorded in the project
            decision log.
          </p>
        </section>

        {/* Status history from the audit log. */}
        <section aria-labelledby="card-history">
          <SectionHeader title="Status history" id="card-history" />
          <div className="tm-card">
            {history.length === 0 ? (
              <p className="tm-muted" style={{ margin: 0 }}>
                No changes have been made to this card yet.
              </p>
            ) : (
              <ol className="tm-timeline">
                {[...history].reverse().map((event) => (
                  <li key={event.id} className="tm-timeline__item">
                    <strong>{auditSummary(event)}</strong>
                    <p className="tm-hint" style={{ margin: '2px 0 0' }}>
                      {event.actorName} · {formatDateTime(event.createdAt)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      </div>

      {/* Lifecycle confirmation. */}
      <ConfirmSheet
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        busy={actionMutation.pending}
        onConfirm={() => {
          if (!pendingAction) return;
          const copy = ACTION_COPY[pendingAction];
          void actionMutation.run(pendingAction).then((outcome) => {
            if (outcome.ok) {
              card.setData(outcome.value);
              toast.show({ message: `${copy.label} done.` });
            } else if (outcome.error) {
              toast.show({
                message: isMockApiError(outcome.error)
                  ? outcome.error.message
                  : 'That did not work. Try again.',
              });
            }
            setPendingAction(null);
          });
        }}
        title={pendingAction ? ACTION_COPY[pendingAction].title : ''}
        body={pendingAction ? ACTION_COPY[pendingAction].body : ''}
        confirmLabel={pendingAction ? ACTION_COPY[pendingAction].confirm : ''}
        {...(pendingAction && ACTION_COPY[pendingAction].danger
          ? { tone: 'danger' as const }
          : {})}
      />

      {/* Spending limit sheet. */}
      <Sheet open={limitOpen} onClose={() => setLimitOpen(false)} title="Change spending limit">
        <div className="tm-field">
          <label className="tm-label" htmlFor="limit-input">
            Monthly limit ({c.currency})
          </label>
          <input
            id="limit-input"
            className="tm-input tm-input--amount"
            inputMode="decimal"
            value={limitValue}
            onChange={(e) => setLimitValue(e.target.value.replace(/[^\d.]/g, ''))}
            aria-invalid={limitError ? true : undefined}
            aria-describedby={limitError ? 'limit-error' : undefined}
          />
          <div className="tm-field__error-slot">
            {limitError ? (
              <span className="tm-field__error" id="limit-error">
                {limitError}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="tm-btn tm-btn--block"
          disabled={limitMutation.pending}
          onClick={() => {
            const minor = Math.round(Number(limitValue) * 100);
            if (!Number.isFinite(minor) || minor < 0) {
              setLimitError('Enter a valid amount.');
              return;
            }
            void limitMutation.run(minor).then((outcome) => {
              if (outcome.ok) {
                card.setData(outcome.value);
                setLimitOpen(false);
                toast.show({ message: 'Spending limit updated.' });
              } else if (isMockApiError(outcome.error)) {
                setLimitError(outcome.error.fieldErrors?.['spendingLimit'] ?? outcome.error.message);
              }
            });
          }}
        >
          {limitMutation.pending ? 'Saving…' : 'Save limit'}
        </button>
      </Sheet>

      {/* Secure reveal sheet. */}
      <Sheet
        open={revealOpen}
        onClose={() => {
          setRevealOpen(false);
          setRevealed(null);
        }}
        title="Confirm it is you"
      >
        {revealed ? (
          <>
            <p className="tm-muted">
              Your card details are shown on the card above. They hide automatically in {countdown}{' '}
              seconds.
            </p>
            <button
              type="button"
              className="tm-btn tm-btn--block"
              style={{ marginBlockStart: 12 }}
              onClick={() => setRevealOpen(false)}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <p className="tm-muted">
              Enter the 6-digit code from your authenticator app to see the full card number.
            </p>
            <div className="tm-field" style={{ marginBlockStart: 12 }}>
              <label className="tm-label" htmlFor="reveal-code">
                Authentication code
              </label>
              <input
                id="reveal-code"
                className="tm-input"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                aria-invalid={codeError ? true : undefined}
                aria-describedby={codeError ? 'reveal-error' : 'reveal-hint'}
                style={{ letterSpacing: '0.4em' }}
              />
              <div className="tm-field__error-slot">
                {codeError ? (
                  <span className="tm-field__error" id="reveal-error">
                    {codeError}
                  </span>
                ) : (
                  <span className="tm-hint" id="reveal-hint">
                    Demo code: 481920
                  </span>
                )}
              </div>
            </div>
            {/* Disabled until the challenge has actually been issued. Without
                this the button is live while `challengeId` is still null, and
                a fast tap is silently swallowed by submitReveal(). */}
            <button
              type="button"
              className="tm-btn tm-btn--block"
              disabled={code.length !== 6 || revealMutation.pending || challengeId === null}
              onClick={() => void submitReveal()}
            >
              {challengeId === null
                ? 'Preparing…'
                : revealMutation.pending
                  ? 'Checking…'
                  : 'Show details'}
            </button>
          </>
        )}
      </Sheet>
    </>
  );
}
